import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import {
  formatIDR,
  formatIDRWithoutSign,
  toCurrencyWithoutSign
} from '@/mixins/filter';
import { formatDate } from '@/utils/helpers';
import moment from 'moment';
import { angularParser } from '@/utils/angularParser';

// The error object contains additional information when logged with JSON.stringify (it contains a properties object containing all suberrors).
// eslint-disable-next-line no-unused-vars
function replaceErrors(key, value) {
  if (value instanceof Error) {
    return Object.getOwnPropertyNames(value).reduce(function(error, keyData) {
      error[keyData] = value[keyData];
      return error;
    }, {});
  }
  return value;
}

function errorHandler(error) {
  // console.log(JSON.stringify({ error: error }, replaceErrors));

  // if (error.properties && error.properties.errors instanceof Array) {
  //   const errorMessages = error.properties.errors
  //     .map(function(error) {
  //       return error.properties.explanation;
  //     })
  //     .join('\n');
  //   // console.log('errorMessages', errorMessages);
  //   // errorMessages is a humanly readable message looking like this :
  //   // 'The tag beginning with "foobar" is unopened'
  // }
  throw error;
}

function formatEventDateRange(item, format = 'DD/MM/YYYY') {
  const { date_start, date_end } = item;
  const startDate = formatDate(date_start, format);
  const endDate = formatDate(date_end, format);

  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
}

function rounder(number) {
  return Math.round(number * 100) / 100;
}

function translatePaymentStageId(item, index, arr) {
  let text = `${rounder(
    item.percentage
  )}% dari Dukungan Dana atau sejumlah ${formatIDR(
    item.amount
  )}, akan dibayarkan setelah tagihan serta dokumen pendukungnya diterima secara benar dan lengkap oleh Pihak Pertama.`;

  if (index + 1 < arr.length) {
    text += '; dan';
  }

  return text;
}

function translatePaymentStageEn(item, index, arr) {
  let text = `${rounder(
    item.percentage
  )}% from the Financial Support or in the amount of IDR ${formatIDRWithoutSign(
    item.amount
  )}, shall be paid after payment & supporting documents have been received correctly and completely by First Party.`;

  if (index + 1 < arr.length) {
    text += '; and';
  }

  return text;
}

export default {
  methods: {
    generateMOU() {
      this.generating = true;

      const url = '/HCF%20-%20MOU.docx';
      const form = this.form;

      PizZipUtils.getBinaryContent(url, (error, content) => {
        if (error) {
          this.generating = false;
          throw error;
        }

        let zip = new PizZip(content);
        let doc;
        try {
          doc = new Docxtemplater(zip, {
            parser: angularParser,
            paragraphLoop: true
          });
        } catch (errorData) {
          // Catch compilation errors (errors caused by the compilation of the template : misplaced tags)
          errorHandler(errorData);
        }

        const company = form.first_party_information.company_name;
        const activities = this.events.map(item => {
          const [en, id] = item.activities.split('/');
          return { en, id };
        });
        const activitiesByLang = lang =>
          activities.map(item => item[lang]).join(', ');

        const mappedEvents = JSON.parse(JSON.stringify(this.events)).map(
          event => {
            const acts = event.activities.split('/');
            event.activities_en = acts[0];
            event.activities_id = acts[1];

            const purpose = event.purpose.split('/');
            event.purpose_en = purpose[0];
            event.purpose_id = purpose[1];

            event.subject_id = `${acts[1]} dalam bidang ${event.purpose_with.bidang}`;
            event.subject_en = `${acts[0]} in the field of ${event.purpose_with.subject}`;

            return event;
          }
        );

        const eventSubjectId = mappedEvents
          .map(event => event.subject_id)
          .join(', ');
        const eventSubjectEn = mappedEvents
          .map(event => event.subject_en)
          .join(', ');

        const hospital = form.second_party_information.hospital_name;
        const hospitalName =
          typeof hospital === 'string' ? hospital : hospital.h_name;
        const vendor = form.second_party_information.vendor_name;
        const vendorName = typeof vendor === 'string' ? vendor : vendor.v_name;

        const MOUDateFormat = 'DD/MM/YYYY';
        const mouData = {
          preval_number_: form.general_information.preval_number,
          created_date_: form.general_information.created_date,
          // Company
          company_name_: company ? company.c_name : '',
          company_address_: form.first_party_information.company_address,
          company_name_of_signatory_:
            form.first_party_information.name_of_signatory,
          company_title_of_signatory_:
            form.first_party_information.title_of_signatory,
          // Hospital
          hospital_name_: hospitalName,
          hospital_description_:
            form.second_party_information.hospital_description,
          hospital_address_: form.second_party_information.hospital_address,
          hospital_telephone_: form.second_party_information.hospital_telephone,
          hospital_fax_: form.second_party_information.hospital_fax,
          hospital_email_: form.second_party_information.hospital_email,
          hospital_name_of_signatory_:
            form.second_party_information.signatory_name,
          hospital_title_of_signatory_:
            form.second_party_information.signatory_title,
          // second_party_information
          vendor_name_: vendorName,
          bank_name_: form.second_party_information.bank_name,
          bank_branch_: form.second_party_information.bank_branch,
          account_number_: form.second_party_information.bank_account_number,
          // Event
          event_name_: this.events.map(item => item.name).join(', '),
          event_date_: this.events
            .map(item => formatEventDateRange(item))
            .join(', '),
          event_venue_: this.events.map(item => item.venue).join(', '),
          event_activities_: this.events
            .map(item => item.activities)
            .join(', '),
          event_activities_id_: activitiesByLang('id'),
          event_activities_en_: activitiesByLang('en'),
          event_purpose_: this.events.map(item => item.purpose),
          event_subject_id_: eventSubjectId,
          event_subject_en_: eventSubjectEn,
          // agreement
          agreement_start_date_: formatDate(
            form.agreement_information.date_start,
            MOUDateFormat
          ),
          agreement_end_date_: formatDate(
            form.agreement_information.date_end,
            MOUDateFormat
          ),
          total_amount_: toCurrencyWithoutSign(
            form.event_information.total_amount
          ),
          payment_stage_id_: this.getPaymentStageId(),
          payment_stage_en_: this.getPaymentStageEn(),
          // Region
          region_address_:
            form.signature_information_of_first_party.region_address,
          region_phone_: form.signature_information_of_first_party.phone_number,
          region_fax_: form.signature_information_of_first_party.fax_number,
          region_manager_email_:
            form.signature_information_of_first_party.email,
          region_manager_name_:
            form.signature_information_of_first_party.pic_name,
          // table
          multiple_table_: this.getMOUTableData()
        };
        // console.log('MOU Data', mouData);
        doc.setData(mouData);
        try {
          // render the document (replace all occurences of {first_name} by John, {last_name} by Doe, ...)
          this.generating = false;
          doc.render();
        } catch (errorData) {
          this.generating = false;
          // Catch rendering errors (errors relating to the rendering of the template : angularParser throws an error)
          errorHandler(errorData);
        }

        const out = doc.getZip().generate({
          type: 'blob',
          mimeType:
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }); //Output the document using Data-URI
        const date = moment().format('DDMMYYYYHHmmss');
        saveAs(out, `MOU-${date}.docx`);
        this.generating = false;
      });
    },
    getPaymentStageId() {
      return this.payments.map(translatePaymentStageId);
    },
    getPaymentStageEn() {
      return this.payments.map(translatePaymentStageEn);
    },
    getMOUTableData() {
      return this.events.map(event => ({
        t_event_name_: event.name,
        t_content_: event.items.map((item, index) => ({
          row_: index + 1,
          item_: item.item,
          price_: toCurrencyWithoutSign(item.price),
          quantity_: item.quantity,
          frequency_: item.frequency,
          amount_: toCurrencyWithoutSign(item.amount)
        })),
        t_total_: toCurrencyWithoutSign(
          event.items.reduce((acc, item) => acc + item.amount, 0)
        )
      }));
    }
  }
};
